import {
  Component,
  effect,
  input,
  model,
  ModelSignal,
  OnInit,
  OnDestroy,
  AfterContentInit,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  signal
} from '@angular/core';
import { outputToObservable, toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { BaseSplitButtonComponent } from './base-split-button.component';
import { SPLIT_BUTTONS_REGISTRATION } from './split-button.contract';
import type { Subscription } from 'rxjs';
import type { ControlValueAccessor } from '@angular/forms';

const DEFAULT_COMPARATOR = <T>(obj1: T, obj2: T): boolean => obj1 === obj2;

@Component({
  selector: 'td-wb-split-buttons',
  templateUrl: './split-buttons.component.html',
  styleUrls: ['./split-buttons.component.scss'],
  providers: [
    ValueAccessorProviderFactory(SplitButtonsComponent),
    { provide: SPLIT_BUTTONS_REGISTRATION, useExisting: SplitButtonsComponent }
  ]
})
export class SplitButtonsComponent<T> implements ControlValueAccessor, OnDestroy, OnInit, AfterContentInit {
  readonly ariaLabel: InputSignal<string | undefined> = input<string>();
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string>();

  private readonly buttonListSignal: WritableSignal<ReadonlyArray<BaseSplitButtonComponent<T>>> = signal([]);
  readonly buttonList: Signal<ReadonlyArray<BaseSplitButtonComponent<T>>> = this.buttonListSignal.asReadonly();
  private readonly buttonList$ = toObservable(this.buttonList);
  readonly comparator: InputSignal<(obj1: T, obj2: T) => boolean> = input<(obj1: T, obj2: T) => boolean>(DEFAULT_COMPARATOR);

  private readonly destroy: Subject<void> = new Subject<void>();

  /**
   * Single source of truth, and the Signal Forms native custom-control binding.
   * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
   */
  readonly value: ModelSignal<T | null | undefined> = model<T | null | undefined>(undefined);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  private readonly disabledState: WritableSignal<boolean> = signal(false);
  private subscriptions: Array<Subscription> = [];
  private id: number = 0;
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  constructor() {
    this.buttonList$.pipe(takeUntil(this.destroy)).subscribe((buttons: ReadonlyArray<BaseSplitButtonComponent<T>>): void => {
      if (buttons.length === 0) {
        return;
      }

      this.assignIdToButton();
      this.onButtonSelect();
      Promise.resolve().then((): void => {
        this.setActiveButtonByValue(this.value());
      });
    });

    // Imperative push of derived state into projected child components. This is the
    // sanctioned use of effect(): syncing signal state to a non-signal (imperative) API.
    // It covers writes arriving through the `[(value)]` model binding; `writeValue()` and
    // user selection sync synchronously so callers observe buttons immediately.
    effect((): void => {
      this.setActiveButtonByValue(this.value());
    });
  }

  ngOnInit(): void {
    this.id++;
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
  }

  ngAfterContentInit(): void {
    this.assignIdToButton();
    this.onButtonSelect();
    this.setActiveButtonByValue(this.value());
  }

  registerButton(button: BaseSplitButtonComponent<T>): void {
    button.setDisabled(this.disabledState());
    this.buttonListSignal.update((current: ReadonlyArray<BaseSplitButtonComponent<T>>): ReadonlyArray<BaseSplitButtonComponent<T>> => [...current, button]);
  }

  unregisterButton(button: BaseSplitButtonComponent<T>): void {
    this.buttonListSignal.update((current: ReadonlyArray<BaseSplitButtonComponent<T>>): ReadonlyArray<BaseSplitButtonComponent<T>> => current.filter((item: BaseSplitButtonComponent<T>): boolean => item !== button));
  }

  getId(): string {
    return 'accessible-split-button-id-' + this.id;
  }

  onButtonSelect(): void {
    this.subscriptions.forEach((subscription: Subscription): void => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
    this.buttonList().forEach((button: BaseSplitButtonComponent<T>): void => {
      this.subscriptions.push(
        outputToObservable(button.selectChange)
          .pipe(
            takeUntil(this.destroy),
            filter((active: boolean): boolean => active)
          )
          .subscribe((): void => {
            if (this.disabledState()) {
              return;
            }
            this.handleUserSelection(button);
          }));
    });
  }

  writeValue(value: T | null | undefined): void {
    this.value.set(value);
    this.setActiveButtonByValue(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
    this.buttonList().forEach((button: BaseSplitButtonComponent<T>): void => {
      button.setDisabled(isDisabled);
    });
  }

  private setActiveButtonByValue(value: T | null | undefined): void {
    if (value !== undefined) {
      const button: BaseSplitButtonComponent<T> | undefined = this.findButtonByValue(value);
      if (button !== undefined && !button.isActive()) {
        this.setActiveButton(button);
      }
    }
  }

  private setActiveButton(button: BaseSplitButtonComponent<T>): boolean {
    this.buttonList().forEach((item: BaseSplitButtonComponent<T>): void => {
      item.setActive(item.getButtonId() === button.getButtonId());
    });
    const val = button.value();
    if (val !== undefined && !Object.is(this.value(), val)) {
      this.value.set(val);
      return true;
    }
    return false;
  }

  private assignIdToButton(): void {
    this.buttonList().forEach((button: BaseSplitButtonComponent<T>, index: number): void => {
      button.setButtonId(index + 1);
      button.setParentId(this.getId());
    });
  }

  private findButtonByValue(value: T): BaseSplitButtonComponent<T> | undefined {
    return this.buttonList()
      .find((button: BaseSplitButtonComponent<T>): boolean => {
        const buttonValue = button.value();
        return buttonValue != null && this.comparator()(buttonValue, value);
      });
  }

  private handleUserSelection(button: BaseSplitButtonComponent<T>): void {
    const changed = this.setActiveButton(button);
    if (changed) {
      this.onChangeCallback(this.value());
    }
    this.onTouchedCallback();
    this.touch.emit();
  }

}
